import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Banner state shape consumed by the shared `<Banner>` primitive (SIM-1370).
 * `variant` lines up 1:1 with `BannerProps["variant"]`.
 */
type SaveNotification = {
  variant: "success" | "error";
  message: string;
};

export interface UseSaveNotificationResult {
  notification: SaveNotification | null;
  saveLabel: string;
  notifySuccess: (message: string) => void;
  notifyError: (message: string) => void;
  notifyErrorFromException: (err: unknown, fallback?: string) => void;
  clear: () => void;
}

const DEFAULT_FLASH_MS = 2000;

/**
 * Banner + save-button state for the "save → show banner → flip label briefly"
 * pattern shared by ProjectDetails / ConnectorDetails / TransformDefinitionDetails.
 */
export function useSaveNotification(options?: {
  flashMs?: number;
  successLabel?: string;
  idleLabel?: string;
}): UseSaveNotificationResult {
  const flashMs = options?.flashMs ?? DEFAULT_FLASH_MS;
  const successLabel = options?.successLabel ?? "✓ Saved";
  const idleLabel = options?.idleLabel ?? "Save";

  const [notification, setNotification] = useState<SaveNotification | null>(null);
  const [saveLabel, setSaveLabel] = useState(idleLabel);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    [],
  );

  const notifySuccess = useCallback(
    (message: string) => {
      setNotification({ variant: "success", message });
      setSaveLabel(successLabel);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setSaveLabel(idleLabel), flashMs);
    },
    [flashMs, idleLabel, successLabel],
  );

  const notifyError = useCallback((message: string) => {
    setNotification({ variant: "error", message });
  }, []);

  const notifyErrorFromException = useCallback((err: unknown, fallback = "Save failed") => {
    setNotification({
      variant: "error",
      message: err instanceof Error ? err.message : fallback,
    });
  }, []);

  const clear = useCallback(() => setNotification(null), []);

  return { notification, saveLabel, notifySuccess, notifyError, notifyErrorFromException, clear };
}
